import { countBy } from 'lodash'
import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import Button from '@/components/Button/Button'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import Checkbox from '@/components/Checkbox/Checkbox'
import FormField from '@/components/FormField'
import InputNumber from '@/components/InputNumber'
import Select, { type SelectOption } from '@/components/Select/Select'
import { callAll } from '@/utils/call-all'
import { mergeOptions } from '@/utils/merge-options'

import styles from './SectionFormLessonBlocks.module.scss'
import type { SectionFormValues } from '../../hooks/useSectionForm'
import type { CurriculumBasketCourseSemester } from '../../queries/useCurriculumBasketCourse'

type FormValues = Pick<
  SectionFormValues,
  'areLessonBlocksEnabled' | 'lessonBlocks'
>

type LessonBlocksProps = {
  semesters: CurriculumBasketCourseSemester[]
}

const SectionFormLessonBlocks = (props: LessonBlocksProps) => {
  const { t } = useTranslation('sections')

  const form = useFormContext<FormValues>()

  const areLessonBlocksEnabled = form.watch('areLessonBlocksEnabled')

  const handleLessonBlocksReset = (value: boolean) => {
    form.setValue('lessonBlocks', value ? [EMPTY_LESSON_BLOCK] : [])
  }

  return (
    <div className={styles.container}>
      <FormField
        id="areLessonBlocksEnabled"
        name="areLessonBlocksEnabled"
        control={form.control}
        render={({ inputProps }) => (
          <Checkbox
            {...inputProps}
            onChange={callAll(inputProps.onChange, handleLessonBlocksReset)}
            label={t('label.enable-lessons-blocks')}
          />
        )}
      />

      {areLessonBlocksEnabled ? <LessonBlocksFields {...props} /> : null}
    </div>
  )
}

const MAX_BLOCK_COUNT = 5

const EMPTY_LESSON_BLOCK = {
  semesterNumber: undefined,
  hours: null
}

const LessonBlocksFields = (props: LessonBlocksProps) => {
  const { t } = useTranslation('sections')

  const form = useFormContext<FormValues>()

  const { fields, append, remove } = useFieldArray({
    name: 'lessonBlocks',
    control: form.control
  })

  const semesterNumbers = form
    .watch(
      fields.map((_, index) => `lessonBlocks.${index}.semesterNumber` as const)
    )
    .filter(Boolean)

  const semesterOptions: SelectOption<number>[] = useMemo(() => {
    const countedSemesterNumbers = countBy(semesterNumbers, 'value')

    return props.semesters.flatMap(({ hours }, index) => {
      const value = index + 1

      if (!hours || countedSemesterNumbers[value] >= MAX_BLOCK_COUNT) return []

      return {
        label: t('label.semester-full', { ORDINAL: value }),
        value
      }
    })
  }, [props.semesters, semesterNumbers, t])

  const addEmptyBlock = () => append(EMPTY_LESSON_BLOCK)

  return (
    <div className={styles.fieldsContainer}>
      {fields.map((field, index) => (
        <div className={styles.field} key={field.id}>
          <FormField
            id={`lessonBlocks.${index}.semesterNumber`}
            name={`lessonBlocks.${index}.semesterNumber`}
            control={form.control}
            render={({ inputProps }) => (
              <Select
                {...inputProps}
                mode="detailed"
                options={mergeOptions(semesterOptions, inputProps.value)}
                placeholder={t('placeholder.select-semester')}
              />
            )}
          />
          <ArrowDownIcon className={styles.icon} aria-hidden="true" />
          <FormField
            id={`lessonBlocks.${index}.hours`}
            name={`lessonBlocks.${index}.hours`}
            control={form.control}
            render={({ inputProps }) => (
              <InputNumber
                {...inputProps}
                placeholder={t('placeholder.number-of-hours')}
              />
            )}
          />
          {fields.length > 1 ? (
            <ButtonIcon
              className={styles.removeButton}
              variant="tertiary"
              size="medium"
              ariaLabel={t('button.remove')}
              onClick={() => remove(index)}
            >
              <DeleteIcon />
            </ButtonIcon>
          ) : null}
        </div>
      ))}

      <Button
        className={styles.addButton}
        size="small"
        onClick={addEmptyBlock}
        variant="secondary"
        icon={<AddIcon />}
      >
        {t('button.add-block')}
      </Button>
    </div>
  )
}

export default SectionFormLessonBlocks
